export interface Challenge {
  title: string
  decision: string
}

export interface ArchitectureStep {
  label: string
  sub?: string
}

export interface Project {
  id: string
  title: string
  kind: string
  role: string
  tagline: string
  repo: string
  problem: string
  whyHard: string
  solution: string
  flowTitle: string
  flow: ArchitectureStep[]
  platform: string[]
  challenges: Challenge[]
  results: string[]
  technologies: string[]
}

export const PROJECT_CATALYST: Project = {
  id: 'catalyst',
  title: 'Project Catalyst',
  kind: 'Engineering Project',
  role: 'Platform Engineering · AWS EKS · GitOps',
  tagline:
    'An internal developer platform on AWS EKS that gives developers self-service infrastructure through Backstage, GitOps and Crossplane.',
  repo: 'https://github.com/ezzddinne/project-catalyst',
  problem:
    'Everyone on the platform needed infrastructure and environments, but managing Kubernetes and cloud resources directly created real operational complexity. Every namespace, environment, database or cloud resource meant a ticket, a handoff, and another human in the loop — slow, inconsistent, and hard to secure.',
  whyHard:
    'The hard part is not creating one cluster — it is making infrastructure repeatable, isolated between teams, and safe for developers to request without giving them keys to the entire cloud account. That requires a control plane between the developer and the infrastructure.',
  solution:
    'An internal developer platform where developers request environments and cloud resources through Backstage. Each request becomes a pull request against a GitOps repository. Argo CD then reconciles it into AWS EKS, and Crossplane provisions the underlying AWS resources — all declarative, all version controlled, all reproducible.',
  flowTitle: 'Developer → Platform → AWS',
  flow: [
    { label: 'Developer', sub: 'requests a namespace / environment / AWS resource through Backstage' },
    { label: 'Backstage', sub: 'self-service portal with software catalog and templates' },
    { label: 'GitHub / GitOps', sub: 'request becomes a pull request in the platform repository' },
    { label: 'Argo CD', sub: 'GitOps controller reconciles the repository into the cluster' },
    { label: 'AWS EKS', sub: 'namespaces, workloads and policies applied live' },
    { label: 'Crossplane', sub: 'provisions the underlying AWS services declaratively' },
  ],
  platform: [
    'Vault — secrets without plaintext',
    'Keycloak / OIDC — federated identity',
    'Istio — mTLS between services',
    'Kyverno — policy enforcement',
    'Karpenter — right-sized nodes',
    'vCluster — virtual clusters for tenant isolation',
    'cert-manager — automated TLS',
    'Prometheus · Grafana · Loki · Tempo — end-to-end observability',
  ],
  challenges: [
    {
      title: 'Developer self-service',
      decision:
        'The Backstage portal is the only interface developers touch. Templates encode the "how", so requests are safe by construction instead of by review.',
    },
    {
      title: 'Multi-tenancy',
      decision:
        'vClusters and dedicated namespaces provide isolation between teams, while Kyverno enforces what workloads are allowed to do anywhere in the control plane.',
    },
    {
      title: 'Infrastructure provisioning',
      decision:
        'Crossplane takes cloud resource requests (databases, buckets, queues) from the GitOps repo and provisions them directly on AWS — no click-ops, no drift.',
    },
    {
      title: 'GitOps delivery',
      decision:
        'Argo CD keeps the cluster converged to the repository. Environments are created, updated or destroyed by merging a pull request.',
    },
    {
      title: 'Secrets management',
      decision:
        'Vault issues short-lived credentials and never stores plaintext secrets in manifests, making the secret surface small and auditable.',
    },
    {
      title: 'Identity',
      decision:
        'Keycloak and OIDC federate identity end to end, so the portal, the cluster and the cloud all trust the same identity source.',
    },
    {
      title: 'Kubernetes isolation',
      decision:
        'Istio mTLS encrypts service-to-service traffic and network policies bound workloads to the few destinations they are allowed to reach.',
    },
    {
      title: 'Cost optimization',
      decision:
        'Karpenter right-sizes and scales nodes to demand, and standby environments can be created and torn down on demand.',
    },
    {
      title: 'Observability',
      decision:
        'Prometheus metrics, Loki logs and Tempo traces are shipped from every workload, so the platform can be operated instead of guessed at.',
    },
  ],
  results: [
    'Developers create or destroy complete environments by merging a pull request — no tickets, no handoffs.',
    'Cloud resources are provisioned declaratively through Crossplane, version controlled like any other code.',
    'Multi-tenant Kubernetes isolation, mTLS, policy and short-lived secrets are enforced by the platform, not by convention.',
    'Federated identity across portal, cluster and cloud removes password sprawl and shared credentials.',
    'The whole stack ships with metrics, logs and traces, so incidents start from telemetry instead of guesswork.',
  ],
  technologies: [
    'AWS EKS',
    'Terraform',
    'Argo CD',
    'Backstage',
    'Crossplane',
    'Karpenter',
    'Vault',
    'Istio',
    'vCluster',
    'Kyverno',
    'Keycloak',
    'OIDC',
    'Prometheus',
    'Grafana',
    'Loki',
    'Tempo',
    'OpenTelemetry',
    'cert-manager',
  ],
}

export const PROJECT_AZURE_ZERO_TRUST: Project = {
  id: 'azure-zero-trust',
  title: 'Azure Zero Trust AKS',
  kind: 'Engineering Project',
  role: 'Cloud Security Architecture · Azure AKS',
  tagline:
    'A private Azure Kubernetes platform behind a hub-and-spoke networking model — secretless, GitOps-driven, and hardened against compromised containers.',
  repo: 'https://github.com/ezzddinne/azure-zero-trust-aks',
  problem:
    'Public Kubernetes clusters expose an unnecessary attack surface. The goal was a private AKS platform where workloads only run where they belong, nothing authenticates with static credentials, and even a compromised container cannot reach the rest of the network.',
  whyHard:
    'Making a cluster private breaks every assumption of a public setup: how pipelines deploy, how workloads reach the internet, how identity reaches the cluster, and how to still observe it all without exposing the control plane.',
  solution:
    'A zero-trust architecture built on hub-and-spoke networking: the hub hosts Azure Firewall and shared services, the spoke hosts a fully private AKS cluster. Workloads authenticate secretlessly with Azure Workload Identity, pipelines deploy through GitHub Actions OIDC, and Argo CD reconciles everything from Git.',
  flowTitle: 'Hub-and-spoke network',
  flow: [
    { label: 'Hub', sub: 'Azure Firewall · shared services · centralized egress' },
    { label: 'Spoke', sub: 'private AKS — no public endpoints' },
    { label: 'Workload Identity', sub: 'secretless authentication into Azure resources' },
    { label: 'GitHub Actions OIDC', sub: 'pipelines authenticate without stored credentials' },
    { label: 'Argo CD', sub: 'GitOps delivery to the private cluster' },
    { label: 'Security ring', sub: 'Trivy · Cosign · Falco · Calico' },
  ],
  platform: [
    'Azure Firewall — only approved egress',
    'Private AKS — no public control plane or workloads',
    'Hub-and-spoke — network segmentation with peered spokes',
    'Azure Workload Identity — secretless runtime auth',
    'GitHub Actions OIDC — keyless pipeline auth',
    'Calico — fine-grained network policies',
    'Trivy + Cosign — images scanned and signed',
    'Falco — runtime behavior detection',
    'Prometheus + Grafana — platform observability',
  ],
  challenges: [
    {
      title: 'Private cluster',
      decision:
        'The AKS control plane and nodes sit in a private spoke with no public endpoints; all traffic enters and leaves through controlled paths.',
    },
    {
      title: 'Network segmentation',
      decision:
        'Hub-and-spoke with Azure Firewall centralizes inspection. Spokes only peer where they must, and Calico policies narrow workload-to-workload traffic.',
    },
    {
      title: 'Secretless authentication',
      decision:
        'Workloads use Azure Workload Identity and pipelines use GitHub Actions OIDC — no long-lived secrets in CI or in the cluster.',
    },
    {
      title: 'Secure supply chain',
      decision:
        'Every image is scanned with Trivy and signed with Cosign; unsigned or vulnerable images are not admitted.',
    },
    {
      title: 'Runtime security',
      decision:
        'Falco watches workload behavior and flags anomalies (shells, unexpected exec, suspicious file access) even after admission.',
    },
    {
      title: 'Cost optimization',
      decision:
        'Private networking plus Calico policies reduce the exposure — and therefore the size — of the security perimeter, while resources are right-sized to demand.',
    },
    {
      title: 'Observability',
      decision:
        'Prometheus and Grafana collect platform and workload metrics, giving the security and SRE teams signal instead of silence.',
    },
  ],
  results: [
    'A fully private AKS platform reachable only through controlled, inspected network paths.',
    'No static credentials in CI, runtime or manifests — identity comes from OIDC and Workload Identity.',
    'Images are scanned and signed before they can run; Falco catches suspicious behavior at runtime.',
    'Hub-and-spoke design keeps management, egress and workloads separated and observable.',
    'Argo CD makes the cluster state fully reproducible from Git.',
  ],
  technologies: [
    'Azure AKS',
    'Azure Firewall',
    'Hub-and-spoke networking',
    'Azure Workload Identity',
    'GitHub Actions OIDC',
    'Argo CD',
    'Trivy',
    'Cosign',
    'Falco',
    'Calico',
    'Prometheus',
    'Grafana',
  ],
}

export const FEATURED_PROJECTS: Project[] = [PROJECT_CATALYST, PROJECT_AZURE_ZERO_TRUST]